import React, { useState, useRef } from 'react';
import { Upload, Download, FileText, AlertTriangle, CheckCircle, X } from 'lucide-react';
import { csvService } from '../../services/csvService';
import LoadingSpinner from '../common/LoadingSpinner';

const CsvImportModal = ({ isOpen, onClose, companyId, onImportComplete }) => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (selectedFile) => {
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setResults(null);
    } else {
      alert('Por favor, selecione um arquivo CSV válido');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    handleFileSelect(selectedFile);
  };

  const downloadTemplate = () => {
    csvService.downloadTemplate();
  };

  const processImport = async () => {
    if (!file || !companyId) return;

    setImporting(true);
    try {
      const importResults = await csvService.processImport(file, companyId);
      setResults(importResults);
      
      if (onImportComplete) {
        onImportComplete(importResults);
      }
    } catch (error) {
      const errorMsg = error.message || 'Erro desconhecido na importação';
      setResults({
        total: 0,
        processed: 0,
        created: 0,
        updated: 0,
        errors: [errorMsg]
      });
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setFile(null);
    setResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const closeModal = () => {
    resetImport();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Importar Clientes via CSV
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Importe múltiplos clientes de uma só vez usando um arquivo CSV
              </p>
            </div>
            <button
              onClick={closeModal}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Template Download */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">
              1. Baixe o modelo
            </h3>
            <button
              onClick={downloadTemplate}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              Baixar Planilha Modelo
            </button>
            <p className="text-sm text-gray-600 mt-2">
              Use este modelo para organizar os dados dos seus clientes
            </p>
          </div>

          {/* File Upload */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">
              2. Selecione o arquivo preenchido
            </h3>
            
            <div
              className={`
                border-2 border-dashed rounded-lg p-8 text-center transition-colors
                ${dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}
                ${file ? 'bg-green-50 border-green-300' : ''}
              `}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="w-8 h-8 text-green-600" />
                  <div>
                    <p className="font-medium text-gray-900">{file.name}</p>
                    <p className="text-sm text-gray-600">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <button
                    onClick={() => setFile(null)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <div>
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-lg font-medium text-gray-900 mb-2">
                    Arraste o arquivo CSV aqui
                  </p>
                  <p className="text-gray-600 mb-4">
                    ou clique para selecionar
                  </p>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Selecionar Arquivo
                  </button>
                </div>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          {/* Import Button */}
          {file && !results && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                3. Processar importação
              </h3>
              <button
                onClick={processImport}
                disabled={importing}
                className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {importing ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Processando...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Iniciar Importação
                  </>
                )}
              </button>
            </div>
          )}

          {/* Results */}
          {results && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                Resultado da Importação
              </h3>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-gray-900">
                      {results.total}
                    </p>
                    <p className="text-sm text-gray-600">Total de Linhas</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">
                      {results.created}
                    </p>
                    <p className="text-sm text-gray-600">Criados</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">
                      {results.updated}
                    </p>
                    <p className="text-sm text-gray-600">Atualizados</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-600">
                      {results.errors.length}
                    </p>
                    <p className="text-sm text-gray-600">Erros</p>
                  </div>
                </div>
              </div>

              {results.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    <h4 className="font-medium text-red-900">
                      Erros Encontrados ({results.errors.length})
                    </h4>
                  </div>
                  <div className="max-h-32 overflow-y-auto">
                    {results.errors.map((error, index) => (
                      <p key={index} className="text-sm text-red-700 mb-1">
                        • {error}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {results.processed > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <p className="font-medium text-green-900">
                      {results.processed} clientes processados com sucesso!
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              <p><strong>Formato esperado:</strong> CSV com separador vírgula</p>
              <p><strong>Operação:</strong> UPSERT (atualiza se existe, cria se não existe)</p>
            </div>
            <div className="flex gap-3">
              {results && (
                <button
                  onClick={resetImport}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Nova Importação
                </button>
              )}
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CsvImportModal;
